import { Button, Card, Image, makeStyles, Text } from "@fluentui/react-components";
import { Checkmark16Regular, ChevronLeft24Regular, ChevronRight24Regular } from "@fluentui/react-icons";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import ICarouselProps from "./ICarouselProps";

const useStyles = makeStyles({
    contentWrapper: {
        display: 'flex',
        justifyContent: 'center',
        position: 'relative',
        width: '100%',
        minHeight: '600px',
        padding: '4rem 1rem',
        boxSizing: 'border-box',
        flexDirection: 'column-reverse',
        
        '@media(min-width: 768px)': {
            flexDirection: 'row',
            padding: '4rem 0'
        },
    },
    innerContainer: {
        position: 'relative',
        display: 'flex',
        justifyContent: 'flex-end',
        maxWidth: '1200px',
        width: '100%',
        padding: '0 1rem',
        boxSizing: 'border-box',
        flexDirection: 'column',
        
        '@media(min-width: 768px)': {
            flexDirection: 'row',
            padding: '0 4rem'
        },
    },
    imgCard: {
        position: "relative",
        width: "100%",
        height: "300px",
        overflow: "hidden",
        borderRadius: "16px",
        marginBottom: "2rem",

        "@media(min-width: 768px)": {
            position: "absolute",
            left: "1rem",
            width: "75%",
            height: "500px",
            marginBottom: "0"
        },
        
        "& img": {
            width: "100%",
            height: "100%",
            objectFit: "cover",
            transition: "opacity 500ms"
        }
    },
    overlay: {
        position: "absolute",
        inset: 0,
        backgroundColor: "rgba(0, 0, 0, 0.4)"
    },
    navigationContainer: {
        position: "absolute",
        bottom: "2rem",
        left: "2rem",
        zIndex: "20",
        display: "flex",
        alignItems: "center",
        gap: "1rem"
    },
    badge: {
        display: "inline-block",
        padding: "0.4rem 0.8rem",
        backgroundColor: "var(--colorBrandBackground2)",
        color: "var(--colorBrandForeground2)",
        borderRadius: "20px",
        fontSize: "12px",
        fontWeight: "var(--fontWeightSemibold)",
        textTransform: "uppercase",
        letterSpacing: "0.5px"
    },
    counter: {
        color: "var(--colorNeutralForegroundInverted)",
        fontWeight: "var(--fontWeightSemibold)"
    },
    contentCard: {
        position: "relative",
        zIndex: "10",
        width: "100%",
        backgroundColor: "var(--colorNeutralBackground1)",
        padding: "2.5rem",
        borderRadius: "16px",
        boxShadow: "0 8px 32px rgba(0, 0, 0, 0.12)",
        marginTop: "-3rem",
        
        "@media(min-width: 768px)": {
            width: "40%",
            marginLeft: "-15%",
            marginTop: "2rem"
        },
    },
    services: {
        display: "flex",
        flexDirection: "column",
        gap: "1rem",
        marginTop: "1rem"
    },
    service: {
        display: 'flex',
        alignItems: 'center',
        gap: '0.75rem',
    },
    checkIcon: {
        color: 'var(--colorBrandForeground1)',
        fontWeight: 'bold',
        fontSize: '16px',
        width: '16px',
        minWidth: '16px',
        maxWidth: '16px',
    }
});

const Service = (props: { service: string }) => {
    const { service } = props;
    const styles = useStyles();

    return (
        <div className={styles.service}>
            <Checkmark16Regular className={styles.checkIcon} />
            <Text>{service}</Text>
        </div>
    );
};

export default function Carousel(props: ICarouselProps) {
    const { t } = useTranslation("general");
    const styles = useStyles();

    const [currentSlideNumber, setCurrentSlide] = useState(0);

    const handlePrevClick = () => setCurrentSlide(prev => prev === 0 ? props.slides.length - 1 : prev - 1);

    const handleNextClick = () => setCurrentSlide(prev => prev === props.slides.length - 1 ? 0 : prev + 1);

    const indicatorText = `${String(currentSlideNumber + 1).padStart(2, '0')} / ${String(props.slides.length).padStart(2, '0')}`;

    const currentSlide = props.slides[currentSlideNumber];

    return (
        <div className={styles.contentWrapper}>
            <div className={styles.innerContainer}>
                <div className={styles.imgCard}>
                    <Image
                        src={currentSlide.image.url}
                        alt={currentSlide.image.altText}
                        />
                    <div className={styles.overlay} />
                    <div className={styles.navigationContainer}>
                        <Button
                            icon={<ChevronLeft24Regular />}
                            appearance="secondary"
                            onClick={handlePrevClick}
                            aria-label={t("previous")}
                        />
                        <Button
                            icon={<ChevronRight24Regular />}
                            appearance="secondary"
                            onClick={handleNextClick}
                            aria-label={t("next")}
                        />
                        <Text as="span" className={styles.counter}>
                            {indicatorText}
                        </Text>
                    </div>
                </div>
                <Card className={styles.contentCard}>
                    <div>
                        <Text as="span" className={styles.badge}>
                            {currentSlide.header}
                        </Text>
                        <Text block as="h3" size={700} weight="semibold">
                            {currentSlide.title}
                        </Text>
                        <Text as="p" size={300}>
                            {currentSlide.description}
                        </Text>
                        <div className={styles.services}>
                            {
                                currentSlide.details?.map((detail, index) => (
                                    <Service key={index} service={detail} />
                                ))
                            }
                        </div>
                    </div>
                </Card>
            </div>
        </div>
    );
}